import { Building2, Eye, XCircle } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  IOneItemInPrepareForRequest,
  ITodayOffersForRequest,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import { useDialogStore } from '@/store';

import AddItemToRequestDialog from './AddItemDialog/AddItemToRequestDialog';

interface OfferHeaderProps {
  client: ITodayOffersForRequest;
  handleResetStage: () => void;
  items: IOneItemInPrepareForRequest[];
  handleAddOneItemLink: (item: IOneItemInPrepareForRequest) => void;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
  handleFinishStage: (value: boolean) => void;
}

// Compact client info component with enhanced styling
const ClientInfo = memo(
  ({
    clientName,
    clientID,
    offerId,
    onViewOffer,
  }: {
    clientName: string;
    clientID: string;
    offerId: string;
    onViewOffer: () => void;
  }) => (
    <div className="group flex items-center rounded-lg bg-white/50 p-1.5 shadow-sm transition-all hover:bg-white/80 dark:bg-gray-800/50 dark:hover:bg-gray-800/80">
      <div className="relative mr-3 flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 text-primary shadow-md transition-transform group-hover:scale-105 dark:bg-gradient-to-br dark:from-primary/30 dark:to-primary/20">
        <Building2 className="h-9 w-9 transition-all" />
        <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-transparent via-transparent to-primary/10 opacity-0 transition-opacity group-hover:opacity-100 dark:to-primary/20"></div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="border-primary/20 bg-primary/5 px-1.5 py-0.5 text-xs font-semibold text-primary shadow-sm"
          >
            {clientID}
          </Badge>
          <h3 className="max-w-[400px] truncate text-lg font-bold text-gray-800 dark:text-gray-200">
            {clientName}
          </h3>
        </div>

        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Button
                onClick={onViewOffer}
                size="sm"
                className="mt-0.5 flex items-center gap-1 p-0 text-xs font-medium text-primary transition-colors hover:bg-transparent hover:text-primary/80"
                variant="link"
              >
                <Eye className="h-3 w-3" />
                {`عرض الشراء  ${offerId.substring(0, 10)}...`}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              <p className="text-xs">عرض تفاصيل العرض</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  ),
);
ClientInfo.displayName = 'ClientInfo';

// Compact action buttons with enhanced styling
const ActionButtons = memo(
  ({
    itemsCount,
    onFinish,
    onReset,
    allItems,
    offerID,
    handleAddOneItemLink,
    handleLoadingStage,
    isLoading,
  }: {
    itemsCount: number;
    onFinish: () => void;
    onReset: () => void;
    allItems: IOneItemInPrepareForRequest[];
    offerID: string;
    handleAddOneItemLink: (item: IOneItemInPrepareForRequest) => void;
    handleLoadingStage: (
      value: boolean,
      type: 'check' | 'upload' | 'update' | 'main',
    ) => void;
    isLoading: boolean;
  }) => (
    <div className="z-10 flex items-center gap-2">
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button
              onClick={onFinish}
              size="sm"
              className={cn(
                'group relative h-9 px-4 py-0 text-sm font-medium transition-all duration-200',
                'border-2 border-primary/20 bg-white/80 shadow-sm backdrop-blur-sm',
                'hover:border-primary/40 hover:bg-white/90 hover:shadow-md',
                'dark:border-primary/50 dark:bg-gray-900/90 dark:hover:border-primary/70 dark:hover:bg-gray-900/95',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                'overflow-visible',
              )}
              variant="outline"
              disabled={itemsCount === 0}
            >
              <span className="dark:text-primary-300 dark:group-hover:text-primary-200 relative z-10 font-semibold text-primary transition-colors group-hover:text-primary/90">
                تجهيز الطلبية
              </span>

              {/* Enhanced animated background */}
              <span className="absolute inset-0 z-0 rounded-md bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 opacity-0 transition-all duration-300 group-hover:opacity-100 dark:from-primary/20 dark:via-primary/30 dark:to-primary/20"></span>

              {itemsCount > 0 && (
                <Badge
                  variant="outline"
                  className="dark:bg-primary-400 absolute -right-4 -top-5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-primary text-lg font-bold text-white shadow-lg transition-all duration-200 hover:scale-110 dark:border-gray-900 dark:text-gray-900"
                >
                  {itemsCount}
                </Badge>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="border border-gray-200/50 bg-white/95 shadow-lg backdrop-blur-sm dark:border-gray-700/50 dark:bg-gray-800/95"
          >
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {itemsCount > 0
                ? `اضغط لتجهيز الطلبية النهائية (${itemsCount} عنصر)`
                : 'أضف عناصر أولاً لتجهيز الطلبية'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AddItemToRequestDialog
        allItems={allItems}
        offerID={offerID}
        handleAddOneItemLink={handleAddOneItemLink}
        handleLoadingStage={handleLoadingStage}
        isLoading={isLoading}
      />

      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Button
              onClick={onReset}
              size="sm"
              className="h-9 gap-1 bg-white px-3 py-0 text-sm font-medium text-red-500 shadow-sm transition-all hover:bg-red-50 hover:text-red-600 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-gray-700"
              variant="outline"
            >
              <XCircle className="h-3.5 w-3.5" />
              الغاء
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">الغاء التجهيز والرجوع للصفحة السابقة</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ),
);
ActionButtons.displayName = 'ActionButtons';

// Order Summary component for better visualization
const OrderSummary = memo(
  ({ items }: { items: IOneItemInPrepareForRequest[] }) => {
    // Memoized calculations using useMemo for better performance
    const summary = useMemo(() => {
      const linkedCount = items.filter((x) => x.HealthyItemID).length;
      const totalCount = items.length;
      const inOrderCount = items.filter((x) => x.Qty > 0).length;

      return {
        linkedCount,
        totalCount,
        inOrderCount,
        linkPercentage:
          totalCount > 0 ? Math.round((linkedCount / totalCount) * 100) : 0,
      };
    }, [items]);

    return (
      <div className="flex items-center gap-3 rounded-lg bg-white/50 px-3 py-1.5 shadow-sm dark:bg-gray-800/30">
        <div className="flex flex-col items-center">
          <span className="text-xs text-muted-foreground">الربط</span>
          <Badge
            variant="outline"
            className={cn(
              'flex items-center justify-center gap-1',
              summary.linkPercentage > 70
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : summary.linkPercentage > 30
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
            )}
          >
            <span>{summary.linkedCount}</span>
            <span className="text-[10px] opacity-70">/</span>
            <span>{summary.totalCount}</span>
          </Badge>
        </div>

        <Separator orientation="vertical" className="h-8" />

        <div className="flex flex-col items-center">
          <span className="text-xs text-muted-foreground">الطلبية</span>
          <Badge
            variant="outline"
            className="bg-primary/10 text-primary dark:bg-primary/20"
          >
            {summary.inOrderCount}
          </Badge>
        </div>
      </div>
    );
  },
);
OrderSummary.displayName = 'OrderSummary';

export const OfferHeader = memo(
  ({
    client,
    handleResetStage,
    items,
    handleAddOneItemLink,
    handleLoadingStage,
    isLoading,
    handleFinishStage,
  }: OfferHeaderProps) => {
    const { openOneOffer } = useDialogStore();

    // Memoized calculations
    const itemsCount = useMemo(
      () => items?.filter((x) => x?.Qty > 0)?.length || 0,
      [items],
    );

    // Memoized handlers with useCallback for better performance
    const handleViewOffer = useCallback(() => {
      if (client?._id) {
        openOneOffer(client._id);
      }
    }, [client?._id, openOneOffer]);

    const handleFinish = useCallback(() => {
      handleFinishStage(true);
    }, [handleFinishStage]);

    // Only re-render the header if these values change
    return (
      <Card className="overflow-visible rounded-lg border border-border/40 bg-gradient-to-br from-slate-50 to-white shadow-md will-change-transform dark:border-gray-800/40 dark:from-gray-900 dark:to-gray-950 dark:shadow-gray-950/10">
        <CardContent className="p-0">
          <div className="flex items-center justify-between gap-3 p-2">
            <div className="flex flex-1 items-center gap-3">
              <ClientInfo
                clientName={client?.ClientName || ''}
                clientID={client?.ClientID?.toString() || ''}
                offerId={client?._id || ''}
                onViewOffer={handleViewOffer}
              />

              {/* Add order summary metrics */}
              <OrderSummary items={items} />
            </div>

            <ActionButtons
              itemsCount={itemsCount}
              onFinish={handleFinish}
              onReset={handleResetStage}
              allItems={items}
              offerID={client?._id || ''}
              handleAddOneItemLink={handleAddOneItemLink}
              handleLoadingStage={handleLoadingStage}
              isLoading={isLoading}
            />
          </div>
        </CardContent>
      </Card>
    );
  },
);

OfferHeader.displayName = 'OfferHeader';
